import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchJobs, fetchJobResults } from '../../../../flux/actions/jobsThunks';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { formatDateTime } from '../../../../utils/formatDate';
import { JOB_STATUS_PRESENTATION } from '../../../../utils/jobPresentation';
import { StatusBadge } from '../../../common/StatusBadge/StatusBadge';
import { JobResultsList } from '../JobResultsList/JobResultsList';
import './ProjectOverview.css';

const SETTLED_JOB_STATUSES = ['succeeded', 'succeeded_with_errors'];
const EMPTY_RESULTS = { status: 'idle', data: [], error: null };

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function LatestCheck({ job, resultsState }) {
    const presentation = job ? JOB_STATUS_PRESENTATION[job.status] : null;

    return (
        <div className="pd-overview-checks">
            <h2 className="pw-page-title">Latest check</h2>

            {!job ? (
                <div className="rules-empty"><p>No successful checks yet.</p></div>
            ) : resultsState.status === 'error' ? (
                <div className="rules-alert rules-alert-error" role="alert">
                    <span>{resultsState.error?.message ?? 'Failed to load results.'}</span>
                </div>
            ) : resultsState.status === 'idle' || resultsState.status === 'loading' ? (
                <p className="projects-loading">Loading results...</p>
            ) : (
                <>
                    <p className="pd-note">
                        <StatusBadge label={presentation.label} variant={presentation.variant} /> Completed {formatDateTime(job.finished_at)}
                    </p>
                    <JobResultsList results={resultsState.data} />
                </>
            )}
        </div>
    );
}

export const ProjectOverview = ({ project, isAdmin }) => {
    const projectId = project.object_id;
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();

    const jobs = useAppSelector(state => state.jobs.byProject[projectId]?.jobs);
    const latestSettledJob = jobs?.data.find(job => SETTLED_JOB_STATUSES.includes(job.status)) ?? null;
    const latestSettledResults = useAppSelector(state =>
        latestSettledJob ? state.jobs.byProject[projectId]?.results[latestSettledJob.object_id] : undefined
    ) ?? EMPTY_RESULTS;

    useEffect(() => {
        if (!isAdmin || jobs) return;
        (async () => {
            try {
                const accessToken = await getAccessToken();
                if (!accessToken) return;
                dispatch(fetchJobs({ accessToken, projectId }));
            } catch (error) {
                console.error('Failed to acquire API token for checks:', error);
            }
        })();
    }, [isAdmin, jobs, dispatch, getAccessToken, projectId]);

    useEffect(() => {
        if (!isAdmin || !latestSettledJob || latestSettledResults.status !== 'idle') return;
        (async () => {
            try {
                const accessToken = await getAccessToken();
                if (!accessToken) return;
                dispatch(fetchJobResults({ accessToken, projectId, jobId: latestSettledJob.object_id }));
            } catch (error) {
                console.error('Failed to acquire API token for check results:', error);
            }
        })();
    }, [isAdmin, latestSettledJob, latestSettledResults.status, dispatch, getAccessToken, projectId]);

    return (
        <div className="pd-overview">
            <dl className="pd-facts">
                <div className="pd-fact">
                    <dt>Name</dt>
                    <dd>{project.name}</dd>
                </div>
                <div className="pd-fact">
                    <dt>Created</dt>
                    <dd>{formatDate(project.created_at)}</dd>
                </div>
                <div className="pd-fact">
                    <dt>Project ID</dt>
                    <dd className="pd-fact-mono">{project.object_id}</dd>
                </div>
            </dl>

            {/* A standing property of the org-admin rule, not a fact about this
                project: admins reach every project their org owns through implicit
                standing and hold no membership row, so they never appear in a
                member list. Stated once here rather than synthesized into rows. */}
            {isAdmin && (
                <p className="pd-note">
                    Organization admins can always access this project, without being a member of it.
                </p>
            )}

            {isAdmin && (
                <LatestCheck job={latestSettledJob} resultsState={latestSettledResults} />
            )}
        </div>
    );
};
